import type { FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { MotionRoot } from '@/components/generals/motion-root'
import { PageHero } from '@/components/generals/page-hero'
import { PageIntro } from '@/components/generals/page-intro'
import { InfoGrid } from '@/components/generals/info-grid'
import { EditorialSplit } from '@/components/generals/editorial-split'
import { EnquiryButton } from '@/components/generals/enquiry-button'
import { MoreLink } from '@/components/generals/more-link'
import { NextPage } from '@/components/generals/next-page'
import { CourseMapSection } from '@/components/landing/course-map-section'
import { SignatureHolesSection } from '@/components/landing/signature-holes-section'
import { ScorecardSection } from '@/components/subpages/scorecard-section'

import { at, library } from '@/constants/media'

import {
    courseIntroStats,
    nextPageAfter,
    pages,
    playingInfo
} from '@/constants/pages'

const page = pages.course
const next = nextPageAfter('course')

const CoursePage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo title={page.seoTitle} description={page.description} ogImage={page.image.src} />

            <MotionRoot>
                <PageHero
                    index={page.index}
                    eyebrow={page.eyebrow}
                    title={page.title}
                    accent={page.accent}
                    image={page.image}
                    facts={page.facts}
                    next='course-intro'
                />

                <PageIntro
                    id='course-intro'
                    index='01'
                    label='The Championship 18'
                    aside='Dune · Pine · Heath · Ocean'
                    statement={<>Every hole at Morrow was found on foot. <em>Two years</em> of walking the estate, in every season and every wind, came before a single line was drawn.</>}
                    body='The Championship 18 plays across three landscapes: out along the coast through dune and umbrella pine, up onto the open heath, and home past the reservoir to the clubhouse. Four sets of tees make it a fair walk for every golfer and a serious test from the back.'
                    stats={courseIntroStats}
                    images={{
                        primary: at(library.lagoonHoles, 'center 60%'),
                        primaryCaption: 'The lagoon holes at dusk, 15 to 17',
                        secondary: at(library.readingThePutt, '36% center'),
                        secondaryCaption: 'Greens that reward a patient read'
                    }}
                />

                <CourseMapSection index='02' />

                <ScorecardSection index='03' />

                <SignatureHolesSection index='04' />

                <EditorialSplit
                    id='short-nine'
                    tone='sand'
                    image={at(library.greenAtSunset, '80% center')}
                    caption='The Short Nine at last light'
                    eyebrow='Beyond the eighteen'
                    title={<>The Short Nine, <em>under the lights.</em></>}
                    body='Nine par threes from 68 to 142 yards, floodlit until eleven at night. It is where first rounds are played, where members settle bets after dinner and where the best short-iron players on the estate are made.'
                    details={['9 holes, par 27', '68 to 142 yards', 'Floodlit until 23:00']}
                    actions={(
                        <EnquiryButton
                            prefill={{ type: 'tee-time', course: 'The Short Nine' }}
                            label='Book the Short Nine'
                        />
                    )}
                />

                <EditorialSplit
                    id='practice-ground'
                    reverse
                    image={library.academyRange}
                    caption='The range bays at dusk'
                    eyebrow='Before the round'
                    title={<>Warm up where <em>every shot is measured.</em></>}
                    body='The range, short game park and putting lab sit five minutes from the first tee. Forty-eight tracked bays tell you exactly how far each club is flying today, before the course asks you to choose.'
                    details={['48 tracked bays', '3 ha short game park', 'Indoor putting lab']}
                    actions={<MoreLink href='/academy' label='Explore the Academy' />}
                />

                <InfoGrid
                    id='playing-morrow'
                    index='05'
                    label='Playing Morrow'
                    heading={<>Everything to know <em>before the first tee.</em></>}
                    intro='Tee times, caddies, carts and the few simple rules that keep a round here unhurried.'
                    items={playingInfo}
                    tone='forest'
                />

                <NextPage
                    href={next.href}
                    index={next.index}
                    title={next.title}
                    teaser={next.teaser}
                    image={next.image}
                />
            </MotionRoot>
        </Layout>
    )
}

export default CoursePage
